import PropTypes from 'prop-types';
import React, { memo, useCallback, useLayoutEffect, useMemo } from 'react';
import _ from 'underscore';
import { Select } from '@bingads-webui-react/primitive';
import { ErrorMessages } from '@bingads-webui-react/validation';
import { LineByLineFormGroup } from '@bingads-webui-cc-react/layout';
import { Constants } from '@bingads-webui-clientcenter/entity-utils';
import { UnsetPiid } from '../constants';
import { i18nPropType, PropTypeExistingPis } from '../shapes';

const ExistingPaymentInstrumentSelectorNoMemo = ({
  i18n,
  supportedPiTypes,
  existingPis,
  paymentMethodErrors,
  piid,
  setPiid,
  setLoading,
  setPiType,
}) => {
  const filteredPiOptions = useMemo(
    () => _.chain(existingPis)
      .filter(({ Type }) => _.contains(supportedPiTypes, Type))
      .map(({ Id, displayName }) => ({ id: Id, value: displayName }))
      .value(),
    [existingPis, supportedPiTypes]
  );

  const selectListItems = useMemo(
    () => _.union(
      [{ id: UnsetPiid, value: i18n.getString(_TL_('Add new payment method')) }],
      filteredPiOptions
    ),
    [i18n, filteredPiOptions]
  );

  const onChange = useCallback(
    ({
      target: { value: newValue },
    }) => {
      if (newValue === UnsetPiid) {
        // set to null here to prevent pidl from loading before effects clear this
        setPiType(Constants.PaymentInstrumentType.Unset);
        setPiid(null);
      } else {
        setPiid(Number(newValue));
      }
    },
    [setPiid, setPiType]
  );

  const selectOptions = useMemo(
    () => selectListItems.map(({ id, value }) => <option key={id} value={id}>{value}</option>),
    [selectListItems]
  );

  useLayoutEffect(
    () => {
      if (piid) {
        if (!_.findWhere(filteredPiOptions, { id: piid })) {
          setPiid(null);
        } else {
          setLoading(false);
        }
      }
    },
    [filteredPiOptions, piid, setLoading, setPiid]
  );

  return filteredPiOptions.length === 0
    ? null
    : (
      <LineByLineFormGroup
        label={i18n.getString(_TL_('Select an existing payment method, or add a new one'))}
        // TODO: help link
      >
        <Select
          value={piid || UnsetPiid}
          onChange={onChange}
          className="piid-selector"
        >
          {selectOptions}
        </Select>
        {/* if piid isn't null, we will show errors below piType selector */}
        {piid !== null && <ErrorMessages messages={paymentMethodErrors} />}
      </LineByLineFormGroup>
    );
};

ExistingPaymentInstrumentSelectorNoMemo.displayName = 'ExistingPaymentInstrumentSelector';
ExistingPaymentInstrumentSelectorNoMemo.propTypes = {
  i18n: i18nPropType.isRequired,
  supportedPiTypes: PropTypes.arrayOf(PropTypes.number).isRequired,
  existingPis: PropTypeExistingPis.isRequired,
  paymentMethodErrors: PropTypes.arrayOf(PropTypes.oneOfType([
    PropTypes.shape({ DangerousHtmlMessage: PropTypes.string }),
    PropTypes.string,
  ])).isRequired,
  piid: PropTypes.number,
  setPiid: PropTypes.func.isRequired,
  setLoading: PropTypes.func.isRequired,
  setPiType: PropTypes.func.isRequired,
};
ExistingPaymentInstrumentSelectorNoMemo.defaultProps = {
  piid: null,
};

export const ExistingPaymentInstrumentSelector = memo(ExistingPaymentInstrumentSelectorNoMemo);
